import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RefreshCw, Undo2, XCircle } from "lucide-react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { AmountMinor } from "@/features/shared/components/amount-minor";
import { useTransactionDispute } from "@/features/wallet/api/use-transaction-dispute";
import { useTransaction } from "@/features/wallet/api/use-transaction";
import type { TransactionDisputeStatus } from "@/features/wallet/api/enums";
import {
  RejectTransactionDisputeDialog,
  TransactionWorkerByReferenceActions,
} from "@/features/wallet/components";

const STATUS_VARIANTS: Record<
  TransactionDisputeStatus,
  "warning" | "success" | "secondary"
> = {
  OPEN: "warning",
  REVERSED: "success",
  REJECTED: "secondary",
};

const STATUS_LABEL_KEYS = {
  OPEN: "wallet.disputes.status.OPEN",
  REVERSED: "wallet.disputes.status.REVERSED",
  REJECTED: "wallet.disputes.status.REJECTED",
} as const satisfies Record<TransactionDisputeStatus, string>;

/**
 * Détail d'une contestation de transfert (`GET /TransactionDisputes/{id}`,
 * guide v3.1) — montants contesté / gelé (`heldAmountMinor`, gel effectif)
 * / non gelé, échéance du gel (72 h), issue. Sur une contestation `OPEN`,
 * deux chemins de résolution (décision 28) :
 *
 * - « Rejeter » (`{id}/reject`, idempotent) : lève le gel, aucune
 *   circulation de fonds → pas de maker-checker ;
 * - « Restituer les fonds » : contre-passation maker-checker
 *   (`TRANSACTION_REVERSAL`) via `Transactions/reverse-by-reference` sur la
 *   référence FPT-… de la transaction — composant
 *   `TransactionWorkerByReferenceActions` (phase 16.6).
 *
 * Route cible : `/app/wallet/disputes/:id` (câblage router/nav : phase 18.7).
 */
export function DisputeDetailPage() {
  const { t } = useTypedTranslation();
  const params = useParams();
  const disputeId = params.id ?? params.disputeId ?? "";
  const {
    data: dispute,
    isLoading,
    error,
    refetch,
  } = useTransactionDispute(disputeId);
  const [rejectOpen, setRejectOpen] = useState(false);

  const backButton = (
    <PageBackButton to="/app/wallet/disputes" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !dispute) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("common.error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      </section>
    );
  }

  const fields: { label: string; value?: string | null }[] = [
    { label: t("wallet.disputes.detail.id"), value: dispute.id },
    {
      label: t("wallet.disputes.detail.transactionId"),
      value: dispute.transactionId,
    },
    {
      label: t("wallet.disputes.detail.beneficiaryWalletId"),
      value: dispute.beneficiaryWalletId,
    },
    {
      label: t("wallet.disputes.detail.openedBy"),
      value: dispute.openedByKeycloakUserId,
    },
    { label: t("wallet.disputes.detail.reason"), value: dispute.reason },
    {
      label: t("wallet.disputes.columns.openedAt"),
      value: dispute.openedAt
        ? formatDateWithFallback(dispute.openedAt, "—")
        : undefined,
    },
    {
      label: t("wallet.disputes.columns.expiresAt"),
      value: dispute.expiresAt
        ? formatDateWithFallback(dispute.expiresAt, "—")
        : undefined,
    },
    {
      label: t("wallet.disputes.detail.closedAt"),
      value: dispute.closedAt
        ? formatDateWithFallback(dispute.closedAt, "—")
        : undefined,
    },
    {
      label: t("wallet.disputes.detail.closedBy"),
      value: dispute.closedByKeycloakUserId,
    },
    {
      label: t("wallet.disputes.detail.resolutionNote"),
      value: dispute.resolutionNote,
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span" className="font-mono text-xl">
              {dispute.transactionReference || dispute.id}
            </NoTranslate>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANTS[dispute.status] ?? "outline"}>
            {t(STATUS_LABEL_KEYS[dispute.status])}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("common.retry")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("wallet.disputes.detail.infoTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("wallet.disputes.detail.contestedAmount")}
              </dt>
              <dd className="font-medium">
                <AmountMinor
                  amountMinor={dispute.contestedAmountMinor}
                  currency={dispute.currency}
                />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("wallet.disputes.detail.heldAmount")}
              </dt>
              <dd className="font-medium">
                <AmountMinor
                  amountMinor={dispute.heldAmountMinor}
                  currency={dispute.currency}
                />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("wallet.disputes.detail.unheldAmount")}
              </dt>
              <dd className="font-medium">
                <AmountMinor
                  amountMinor={dispute.unheldAmountMinor}
                  currency={dispute.currency}
                />
              </dd>
            </div>
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span" className="break-all">
                    {value || "—"}
                  </NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
          {dispute.transactionId ? (
            <div className="mt-4">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/app/wallet/transactions/${dispute.transactionId}`}>
                  {t("wallet.disputes.detail.viewTransaction")}
                </Link>
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {dispute.status === "OPEN" ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("wallet.disputes.resolution.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t("wallet.disputes.resolution.description")}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <RequireRole
                  roles={[FLASHPAY_ROLES.ADMIN, FLASHPAY_ROLES.SUPERADMIN]}
                >
                  <Button
                    variant="destructive"
                    onClick={() => setRejectOpen(true)}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    {t("wallet.disputes.resolution.reject")}
                  </Button>
                </RequireRole>
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <Undo2 className="h-4 w-4" />
                  {t("wallet.disputes.resolution.reverseHint")}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Restitution des fonds : contre-passation maker-checker par
              référence (le composant n'affiche l'action reverse que sur un
              COMPLETED et la réserve aux rôles service/admin/superadmin). */}
          <DisputeReverseByReference transactionId={dispute.transactionId} />

          <RejectTransactionDisputeDialog
            dispute={dispute}
            open={rejectOpen}
            onOpenChange={setRejectOpen}
          />
        </>
      ) : null}
    </section>
  );
}

/** Charge la transaction contestée puis délègue la contre-passation au composant worker par référence. */
function DisputeReverseByReference({ transactionId }: { transactionId: string }) {
  const { data: transaction } = useTransaction(transactionId);
  if (!transaction) return null;
  return <TransactionWorkerByReferenceActions transaction={transaction} />;
}
